import React, { useState } from 'react'
import { useApiListOrgSsoRecipient, useApiFormOrgSsoRecipient } from '../../../../../api-new/state/auth'
import { useLoad } from '../../../../../shared/hooks/load'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

const EMAIL = /^[^\s@,<>]+@[^\s@,<>]+\.[^\s@,<>]+$/

// Org-owned alert recipients (Task 21): who hears about this organization's
// own domain lifecycle and credential-expiry alerts. GET|POST
// /org-sso/recipients, DELETE /org-sso/recipients/:id -- scopeType, scopeID
// and category are forced server-side to this organization + "sso", so the
// add form sends only address and label. Mounted only from org-sso.js,
// which is itself gated on the plan. Workspace language (review I5).
export const OrgSsoRecipient = ({ enabled }) => {
  const apiListOrgSsoRecipient = useApiListOrgSsoRecipient()
  const orgSsoRecipient = useApiFormOrgSsoRecipient()

  const [address, setAddress] = useState('')
  const [label, setLabel] = useState('')
  const [hint, setHint] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  useLoad(() => apiListOrgSsoRecipient.get(), [])

  const handleRefresh = async () => await apiListOrgSsoRecipient.get()

  const handleAdd = async e => {
    e.preventDefault()
    setError(null)

    if (!EMAIL.test(address.trim())) {
      setHint(address.trim() ? 'Enter one valid email address.' : 'Enter the address alerts should go to.')
      return
    }

    setHint(null)
    setBusy(true)
    try {
      await orgSsoRecipient.postArg('', { address: address.trim(), label: label.trim() })
      setAddress('')
      setLabel('')
      await handleRefresh()
    } catch (err) {
      if (err && err.status === 409) setError((err.data && err.data.message) || 'This address already gets these alerts.', err)
      else if (err && err.status === 400) setError((err.data && err.data.message) || 'Enter a valid email address.', err)
      else if (err && err.status === 403) setError((err.data && err.data.message) || "Your organization's plan does not include single sign-on.", err)
      else setError((err && err.data && err.data.message) || "The recipient couldn't be added.", err)
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async id => {
    await orgSsoRecipient.delete(id)
    await handleRefresh()
  }

  const recipients = apiListOrgSsoRecipient.items

  return (
    <div className="d-grid gap-3">
      <form className="d-flex flex-wrap align-items-start gap-2" noValidate onSubmit={handleAdd}>
        <div className="sw-field flex-grow-1" style={{ maxWidth: 320 }}>
          <label className="sw-label" htmlFor="org-sso-recipient-address">Email address</label>
          <input id="org-sso-recipient-address" type="email" className="form-control" value={address} placeholder="it-alerts@example.org" autoComplete="off"
            disabled={!enabled} aria-describedby="org-sso-recipient-address-hint" onChange={e => setAddress(e.target.value)} />
          <div id="org-sso-recipient-address-hint" className={hint ? 'sw-required' : 'sw-hint'}>
            {hint || 'Your domain and credential alerts go to these addresses. A shared inbox or distribution list is better than one person.'}
          </div>
        </div>
        <div className="sw-field" style={{ maxWidth: 220 }}>
          <label className="sw-label" htmlFor="org-sso-recipient-label">Label <span className="sw-hint fw-normal ms-1">Optional</span></label>
          <input id="org-sso-recipient-label" className="form-control" value={label} placeholder="IT help desk" autoComplete="off"
            disabled={!enabled} onChange={e => setLabel(e.target.value)} />
        </div>
        <button type="submit" className="btn sw-btn sw-btn-primary" style={{ marginTop: '1.6rem' }} disabled={!enabled || busy}>Add recipient</button>
      </form>

      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-0" />}

      {recipients.length === 0
        ? <p className="sw-hint m-0">No alert recipients yet. Add the address your IT team watches.</p>
        : <ul className="sc-rows">
          {recipients.map(recipient =>
            <li key={recipient.id} className="sc-row">
              <div className="sc-row-main">
                <span className="fw-semibold">{recipient.address}</span>
                {recipient.label && <span className="sw-hint">{recipient.label}</span>}
              </div>
              <button type="button" className="btn btn-sm sw-btn-outline-danger" disabled={!enabled} onClick={() => handleRemove(recipient.id)}>
                Remove<span className="visually-hidden"> {recipient.address}</span>
              </button>
            </li>)}
        </ul>}
    </div>
  )
}
